import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import accountService from "../../services/account/account.service";
import { useAuth } from "../../utils/auth";
import { UpdateProfileDto } from "../../services/account/account.dto";
import FormikValueIsValid from "../../utils/FormikValueIsValid";
import { useToast } from "../../utils/toast";
import { InputMask } from "primereact/inputmask";
import DropdownDatas from "../../utils/DropdownValues";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { InputSwitchChangeEvent, InputSwitch } from "primereact/inputswitch";
import { Role } from "../../services/auth/auth.dto";

const ProfilePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const [filteredCountries, setFilteredCountries] = useState<any[]>([]); // Filtrelenen listeyi tutar
  const [selectedOSB, setSelectedOSB] = useState<any>(null);
  const allCountries = DropdownDatas.getOSBList();
  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    accountService.GetUserInformation().then((res) => {
      if (res && res.result?.data) {
        formik.setFieldValue("first_name", res.result.data.first_name);
        formik.setFieldValue("last_name", res.result.data.last_name);
        formik.setFieldValue("email", res.result.data.email);
        formik.setFieldValue("phone_number", res.result.data.phone_number);
        formik.setFieldValue("osb", res.result.data.osb);
        setSelectedOSB(res.result.data.osb);
        formik.setFieldValue("osb_gorevi", res.result.data.osb_gorevi);
      }
    });
  }, [auth]);

  const formik = useFormik<UpdateProfileDto>({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      osb: "",
      osb_gorevi: "",
      request_osbuk_user: false
    },
    enableReinitialize: true,
    validate: (data) => {
      const errors: any = {};
      if (!data.first_name) {
        errors.first_name = "Ad gereklidir";
      }

      if (!data.last_name) {
        errors.last_name = "Soyad gereklidir";
      }

      if (!data.email) {
        errors.email = "E-posta adresi gereklidir";
      }

      if (!data.phone_number) {
        errors.phone_number = "Telefon numarası gereklidir";
      }

      if (!data.osb) {
        errors.osb = "OSB gereklidir";
      }

      if (!data.osb_gorevi) {
        errors.osb_gorevi = "OSB Görevi gereklidir";
      }

      return errors;
    },
    onSubmit: (values) => {
      values.request_osbuk_user = checked;
      values.phone_number = values.phone_number
        .replace(/_/g, "")
        .replace(/ /g, "")
        .replace(/\(/g, "")
        .replace(/\)/g, "")
        .replace(/-/g, "");
      accountService.UpdateProfile(values).then((res) => {
        if (!res?.result?.error) {
          toast.show("Profil başarıyla güncellendi", "success");
          //navigate("/");
        } else {
          toast.show(res?.result?.error || "Kayıt işlemi sırasında bir hata oluştu.", "error");
        }
      });
    }
  });

  const search = (event: { query: string }) => {
    const query = event.query.toLowerCase();
    const filtered = allCountries.filter((item) => item.label.toLowerCase().includes(query));
    setFilteredCountries(filtered);
  };

  const confirmRequestOsbukUser = async (check: InputSwitchChangeEvent) => {
    if (check.value) {
      if (check.originalEvent) {
        confirmPopup({
          target: check.originalEvent.target as HTMLElement,
          message:
            "Tam yetki talebinde bulunuyorsunuz. Kaydınız yönetici tarafından onaylandığında tam yetkili kullanıcı olacaksınız.",
          icon: "pi pi-info-circle",
          defaultFocus: "accept",
          acceptClassName: "p-button-success",
          acceptLabel: "Onayla",
          rejectLabel: "İptal",
          accept: async () => {
            setChecked(true);
          },
          reject: () => {
            setChecked(false);
          }
        });
      }
    } else {
      setChecked(false);
    }
  };

  return (
    <div className="flex justify-content-center py-2 sm:py-4">
      <form
        className="w-full"
        style={{ maxWidth: "860px" }}
        onSubmit={formik.handleSubmit}
      >
        <div className="card border-round-2xl shadow-2 p-4 sm:p-5 md:p-6">
          <div className="text-center mb-4 sm:mb-5">
            <Link to="/app/homepage">
              <img src="/images/logoosbuk.png" alt="OSBÜK Logo" height="48" className="mb-2" />
            </Link>
            <div className="text-900 text-2xl sm:text-3xl font-bold mb-2">Profilimi Güncelle</div>
            <span className="text-600 text-sm sm:text-base">Bu ekrandan profil bilgilerinizi güncelleyebilirsiniz.</span>
          </div>

          <div className="grid">
            <div className="col-12 md:col-6">
              <label htmlFor="first_name" className="text-900 font-medium block mb-1">
                İsim
              </label>
              <InputText
                id="first_name"
                type="text"
                placeholder="İsim"
                className={classNames("w-full p-3", {
                  "p-invalid": FormikValueIsValid(formik, "first_name")
                })}
                value={formik.values.first_name}
                onChange={(e) => formik.setFieldValue("first_name", e.target.value)}
              />
              {FormikValueIsValid(formik, "first_name") && (
                <div className="p-error mt-2 text-sm">{formik.errors.first_name}</div>
              )}
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="last_name" className="text-900 font-medium block mb-1">
                Soyisim
              </label>
              <InputText
                id="last_name"
                type="text"
                placeholder="Soyisim"
                className={classNames("w-full p-3", {
                  "p-invalid": FormikValueIsValid(formik, "last_name")
                })}
                value={formik.values.last_name}
                onChange={(e) => formik.setFieldValue("last_name", e.target.value)}
              />
              {FormikValueIsValid(formik, "last_name") && (
                <div className="p-error mt-2 text-sm">{formik.errors.last_name}</div>
              )}
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="email" className="text-900 font-medium block mb-1">
                E-Posta Adresi
              </label>
              <InputText
                id="email"
                type="email"
                placeholder="E-Posta Adresi"
                className={classNames("w-full p-3", {
                  "p-invalid": FormikValueIsValid(formik, "email")
                })}
                value={formik.values.email}
                onChange={(e) => formik.setFieldValue("email", e.target.value)}
              />
              {FormikValueIsValid(formik, "email") && (
                <div className="p-error mt-2 text-sm">{formik.errors.email}</div>
              )}
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="phone_number" className="text-900 font-medium block mb-1">
                Telefon Numarası
              </label>
              <InputMask
                id="phone_number"
                keyfilter="int"
                mask="(0999) 999-99-99"
                placeholder="Telefon Numarası"
                className={classNames("w-full p-3", {
                  "p-invalid": FormikValueIsValid(formik, "phone_number")
                })}
                value={formik.values.phone_number}
                onChange={(e) => formik.setFieldValue("phone_number", e.target.value)}
              />
              {FormikValueIsValid(formik, "phone_number") && (
                <div className="p-error mt-2 text-sm">{formik.errors.phone_number}</div>
              )}
            </div>

            <div className="col-12 md:col-6">
              <label className="text-900 font-medium block mb-1" htmlFor="osb">
                Bağlı Olduğunuz OSB
              </label>
              <AutoComplete
                id="osb"
                name="osb"
                inputId="osb"
                value={selectedOSB}
                suggestions={filteredCountries}
                completeMethod={search}
                field="label"
                onChange={(e: AutoCompleteChangeEvent) => {
                  setSelectedOSB(e.value);
                  formik.setFieldValue("osb", e.value.value ? e.value.value : e.value);
                }}
                placeholder="Bağlı Olduğunuz OSB"
                dropdown
                className={classNames("w-full", {
                  "p-invalid": FormikValueIsValid(formik, "osb")
                })}
              />
              {FormikValueIsValid(formik, "osb") && (
                <div className="p-error mt-2 text-sm">{formik.errors.osb}</div>
              )}
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="osb_gorevi" className="text-900 font-medium block mb-1">
                OSB'deki Göreviniz
              </label>
              <InputText
                id="osb_gorevi"
                type="string"
                placeholder="OSB'deki Göreviniz"
                className={classNames("w-full p-3", {
                  "p-invalid": FormikValueIsValid(formik, "osb_gorevi")
                })}
                value={formik.values.osb_gorevi}
                onChange={(e) => formik.setFieldValue("osb_gorevi", e.target.value)}
              />
              {FormikValueIsValid(formik, "osb_gorevi") && (
                <div className="p-error mt-2 text-sm">{formik.errors.osb_gorevi}</div>
              )}
            </div>

            {auth.getUser().role === Role.USER && !auth.getUser().request_osbuk_user && (
              <div className="col-12 flex align-items-center justify-content-center w-full my-3 gap-2">
                <InputSwitch
                  id="profileRoleSwitch"
                  checked={checked}
                  onChange={async (e: InputSwitchChangeEvent) => await confirmRequestOsbukUser(e)}
                />
                <label htmlFor="profileRoleSwitch" className="text-sm text-700 cursor-pointer user-select-none">
                  Sebis.tr sitemizde tam yetki talebinde bulunabilirsiniz.
                </label>
                <ConfirmPopup />
              </div>
            )}

            <div className="w-full col-12 mt-3">
              <Button label="Güncelle" className="w-full p-3 text-lg font-semibold" type="submit" />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfilePage;
